import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';

import ROUTES, { makeGroupSearchURL, makeProfileURL } from 'routes';
import { useAppDispatch, useAppSelector, useClickOutside } from 'hooks';
import { selectAccount } from 'redux/account/selectors';
import { logout } from 'redux/auth/actions';
import Icon from 'components/Icon';
import Typography from 'components/ui/Typography';
import UserSnippetWithAvatar from 'components/UserSnippetWithAvatar';
import { SpriteIcon } from 'types/icons';

import css from './Sidebar.module.scss';

interface Props {
    sidebarActive: boolean;
    setSidebarActive: (value: boolean) => void;
}

interface SidebarItem {
    icon: SpriteIcon;
    text: string;
    route?: string | Partial<Location>;
    action?: () => void;
}

const Sidebar: React.FC<Props> = ({ sidebarActive, setSidebarActive }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const account = useAppSelector(selectAccount);

    const sidebarItems: SidebarItem[] = [
        {
            icon: 'user',
            text: t('AccountMenu.MyFriends'),
            route: ROUTES.FRIENDS,
        },
        { icon: 'users-three', text: t('AccountMenu.Groups'), route: makeGroupSearchURL() },
        {
            icon: 'bookmark',
            text: t('AccountMenu.Bookmarks'),
            route: ROUTES.BOOKMARKS,
        },
        {
            icon: 'settings-gear',
            text: t('Common.Sections.Settings'),
            route: ROUTES.SETTINGS,
        },
        {
            icon: 'support-lifesaver',
            text: t('Common.Sections.Support'),
            route: ROUTES.SUPPORT,
        },
        {
            icon: 'logout',
            text: t('AccountMenu.Logout'),
            action: () => dispatch(logout()),
        },
    ];
    const sidebarRef = useRef(null);

    useClickOutside(sidebarRef, () => setSidebarActive(false));

    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    const minSwipeDistance = 25;

    const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        setTouchEnd(null);
        if ('touches' in e) {
            setTouchStart(e.touches[0].clientX);
        }
    };

    const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        if ('touches' in e) {
            setTouchEnd(e.touches[0].clientX);
        }
    };

    const onTouchEnd = () => {
        if (touchStart && touchEnd) {
            const distance = touchStart - touchEnd;
            const isRightSwipe = distance < -minSwipeDistance;
            if (isRightSwipe) setSidebarActive(false);
        }
    };

    return (
        <div
            className={clsx(css.container, sidebarActive && css['active'])}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            ref={sidebarRef}
        >
            {account && (
                <div
                    className={css.userInfo}
                    onClick={() => {
                        navigate(makeProfileURL(account.username));
                        setSidebarActive(false);
                    }}
                >
                    <UserSnippetWithAvatar
                        omitCountry
                        user={account}
                        avatarSize="M"
                        infoText={t('Sidebar.ViewProfile')}
                    />
                </div>
            )}
            {sidebarItems.map((item) => (
                <div
                    onClick={
                        !item.action
                            ? () => {
                                  item.route && navigate(item.route);
                                  setSidebarActive(false);
                              }
                            : item.action
                    }
                    key={item.icon}
                    className={css.menuItem}
                >
                    <Icon name={item.icon} width={20} color="gray3" />
                    <Typography className={css.text}>{item.text}</Typography>
                </div>
            ))}
        </div>
    );
};

export default React.memo(Sidebar);
